/**
 * Back in Stock (AMP) trigger for Horizon.
 *
 * The AMP widget (backinstock.useamp.com) auto-detects the add-to-cart button
 * via legacy/Dawn selectors (.product-form__add-to-cart, [js-product-form],
 * select.single-option-selector, [data-bis-app-block]). None of those exist
 * in Horizon — we use Component-style markup with `ref="addToCartButton"`
 * and radio-input variant pills — so the popup never gets bound.
 *
 * This file fills the gap: while a sold-out size is selected the product
 * page shows an inline email sign-up above the button (submitted through the
 * app); where that isn't available, picking a sold-out size or clicking the
 * button opens AMP's popup through its public `window.BISPopover` API.
 *
 * Loaded by layout/theme.liquid after `back-in-stock-helper`.
 */
(function () {
  var ATC_SELECTOR = '.add-to-cart-button button, button.add-to-cart-button';
  var VARIANT_INPUT_SELECTOR = 'input[ref="variantId"]';
  var POPUP_RETRY_DELAY = 100;
  var POPUP_RETRY_LIMIT = 50;

  function getVariantId(button) {
    var form = button.closest('form');
    var input = form && form.querySelector(VARIANT_INPUT_SELECTOR);
    return input ? input.value : '';
  }

  function isSoldOut(variantId) {
    if (!window._BISConfig || !window._BISConfig.product) return false;
    var variants = window._BISConfig.product.variants || [];
    var variant = variants.find(function (v) {
      return String(v.id) === String(variantId);
    });
    return variant && variant.available === false;
  }

  function getPopup() {
    // AMP's widget runs inside an IIFE. `BIS` is private; `BISPopover` is the
    // public object it intentionally exposes after initialization.
    return window.BISPopover || (window.BIS && window.BIS.popup);
  }

  function openPopup(variantId, attempt) {
    var popup = getPopup();
    var retryCount = attempt || 0;

    if (!popup || !popup.ready) {
      if (retryCount < POPUP_RETRY_LIMIT) {
        window.setTimeout(function () {
          openPopup(variantId, retryCount + 1);
        }, POPUP_RETRY_DELAY);
      } else {
        console.warn('[BIS trigger] popup did not become available.');
      }
      return;
    }

    popup.ready.then(function () {
      try {
        popup.show({ variantId: variantId });
      } catch (err) {
        console.warn('[BIS trigger] popup.show failed:', err);
      }
    });
  }

  /*
   * Inline sign-up (blocks/buy-buttons.liquid, [data-bis-inline]): while a
   * sold-out size is selected, an email field sits above the button, and the
   * button signs the customer up through the app (the same endpoint and
   * payload its popup uses) instead of opening the popup. The popup remains
   * the fallback when the inline panel or the app's internals aren't
   * available, or reCAPTCHA is on.
   */
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var SIGNUP_TIMEOUT = 15000;

  function inlinePanel(button) {
    var block = button.closest('.buy-buttons-block');
    return block && block.querySelector('[data-bis-inline]');
  }

  function canSignUpInline() {
    var popup = getPopup();
    return Boolean(
      popup &&
        popup.ready &&
        popup.ajaxOpts &&
        typeof popup._buildCreatePayload === 'function' &&
        window.BIS &&
        typeof window.BIS.requestJSONP === 'function' &&
        !(popup.settings && popup.settings.recaptcha_enabled)
    );
  }

  function selectedSizeLabel(panel) {
    var size = document.querySelector('variant-picker [data-size-option] input[type="radio"]:checked');
    return size ? (panel.dataset.sizePrefix || '') + size.value : '';
  }

  function setError(panel, message) {
    var error = panel.querySelector('[data-bis-inline-error]');
    var input = panel.querySelector('.bis-inline__email');
    if (error) {
      error.textContent = message || '';
      error.hidden = !message;
    }
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  function resetPanel(panel) {
    var block = panel.closest('.buy-buttons-block');
    panel.querySelector('[data-bis-inline-form]').hidden = false;
    panel.querySelector('[data-bis-inline-done]').hidden = true;
    if (block) block.classList.remove('bis-inline-signed-up');
    setError(panel, '');
  }

  function syncPanels() {
    document.querySelectorAll('.buy-buttons-block').forEach(function (block) {
      var panel = block.querySelector('[data-bis-inline]');
      var button = block.querySelector('button[ref="addToCartButton"]');
      if (!panel || !button) return;

      var soldOut = button.dataset.backInStock === 'true';
      var variantId = getVariantId(button);
      if (panel.dataset.variantId !== variantId) {
        panel.dataset.variantId = variantId;
        resetPanel(panel);
      }
      panel.hidden = !soldOut;

      var input = panel.querySelector('.bis-inline__email');
      var customer = window._BISConfig && window._BISConfig.customer;
      if (input && !input.value && customer && customer.email) input.value = customer.email;
    });
  }

  function signUp(email, variantId, acceptsMarketing) {
    var popup = getPopup();
    var productId = window._BISConfig && window._BISConfig.product && window._BISConfig.product.id;
    return new Promise(function (resolve, reject) {
      var timer = window.setTimeout(function () {
        reject(new Error('timeout'));
      }, SIGNUP_TIMEOUT);
      popup.ready.then(function () {
        try {
          window.BIS.extend(popup.ajaxOpts, { key: 'callback' });
          var payload = popup._buildCreatePayload(email, variantId, productId, {
            accepts_marketing: acceptsMarketing,
            quantity_required: 1,
          });
          window.BIS.requestJSONP(popup.ajaxOpts.url, payload).then(function (response) {
            window.clearTimeout(timer);
            resolve(response || {});
          });
        } catch (err) {
          window.clearTimeout(timer);
          reject(err);
        }
      });
    });
  }

  function submitInline(panel, button, variantId) {
    var input = panel.querySelector('.bis-inline__email');
    var optin = panel.querySelector('[data-bis-inline-optin]');
    var email = input ? input.value.trim() : '';

    if (!EMAIL_PATTERN.test(email)) {
      setError(panel, panel.dataset.invalidEmail);
      // The tap on the button (or the phone's buy bar) is a user gesture, so
      // focusing here also opens the keyboard on phones.
      input.focus({ preventScroll: true });
      input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    if (panel.dataset.submitting === 'true') return;

    setError(panel, '');
    panel.dataset.submitting = 'true';
    button.setAttribute('aria-busy', 'true');

    signUp(email, variantId, Boolean(optin && optin.checked))
      .then(function (response) {
        if (response.status !== 'OK') {
          var messages = [];
          for (var key in response.errors || {}) messages.push(response.errors[key]);
          setError(panel, messages.join(' ') || panel.dataset.error);
          return;
        }
        var done = panel.querySelector('[data-bis-inline-done]');
        done.textContent = (panel.dataset.done || '')
          .replace('[email]', email)
          .replace('[size]', selectedSizeLabel(panel) || 'it');
        panel.querySelector('[data-bis-inline-form]').hidden = true;
        done.hidden = false;
        var block = panel.closest('.buy-buttons-block');
        if (block) block.classList.add('bis-inline-signed-up');
      })
      .catch(function () {
        setError(panel, panel.dataset.error);
      })
      .then(function () {
        panel.dataset.submitting = 'false';
        button.removeAttribute('aria-busy');
      });
  }

  var syncPending = false;
  function scheduleSync() {
    if (syncPending) return;
    syncPending = true;
    window.requestAnimationFrame(function () {
      syncPending = false;
      syncPanels();
    });
  }

  // Product pages only: watch the product form for the sold-out state.
  var hasInlinePanel = Boolean(document.querySelector('[data-bis-inline]'));
  if (hasInlinePanel) document.addEventListener('variant:update', scheduleSync);
  if (hasInlinePanel) new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var target = records[i].target;
      if (target.nodeType === 1 && target.closest && target.closest('product-form-component')) {
        scheduleSync();
        return;
      }
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-back-in-stock'],
  });
  if (hasInlinePanel) syncPanels();

  // Enter in the email field submits through the same path as the button.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-bis-inline-form]');
    if (!form) return;
    event.preventDefault();
    var block = form.closest('.buy-buttons-block');
    var button = block && block.querySelector('button[ref="addToCartButton"]');
    if (button) button.click();
  });

  // No inline panel (e.g. quick add): clicking or tapping a sold-out size
  // opens the popup straight away. Pointer picks only, so keyboard users
  // arrowing through sizes don't get a popup per size.
  var POINTER_PICK_WINDOW = 3000;
  var lastPointerPick = 0;
  var lastPickPanel = null;

  document.addEventListener(
    'pointerdown',
    function (event) {
      var picker = event.target.closest('variant-picker');
      if (!picker) return;
      lastPointerPick = Date.now();
      var section = picker.closest('.shopify-section, dialog, product-card');
      lastPickPanel = section && section.querySelector('[data-bis-inline]');
    },
    true
  );

  document.addEventListener('variant:update', function (event) {
    var variant = event.detail && event.detail.resource;
    if (!variant || variant.available !== false) return;
    if (Date.now() - lastPointerPick > POINTER_PICK_WINDOW) return;
    lastPointerPick = 0;
    if (lastPickPanel && canSignUpInline()) return;
    openPopup(variant.id);
  });

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest(ATC_SELECTOR);
      if (!button) return;

      var variantId = getVariantId(button);
      if (!variantId || !isSoldOut(variantId)) return;

      // A sold-out variant must never reach Shopify's cart endpoint.
      event.preventDefault();
      event.stopPropagation();

      var panel = inlinePanel(button);
      if (panel && !panel.hidden && canSignUpInline()) {
        var done = panel.querySelector('[data-bis-inline-done]');
        // Already signed up (e.g. the phone's buy bar tapped again): show it.
        if (!done.hidden) {
          done.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        submitInline(panel, button, variantId);
        return;
      }
      // Queue the popup briefly if AMP is still initializing.
      openPopup(variantId);
    },
    true
  );
})();
